/**
 * Stitches Shadow DOM Support
 *
 * Utilities for creating Stitches instances that inject styles into Shadow DOM.
 * This enables Sparky components to work correctly inside Shadow DOM environments.
 */

import { normalizeCSS } from '../normalize';
import {
  config,
  createTheme,
  css,
  defaultTheme,
  getCssText,
  globalStitches,
  keyframes,
  styled,
} from '../stitches.config';
import { globalStyles, themes } from '../themes';
import { Theme } from '../types';

/**
 * Create a Stitches instance that injects styles into a Shadow DOM root.
 *
 * @param shadowRoot - The Shadow DOM root to inject styles into
 * @param selectedTheme - The theme to use (defaults to 'default')
 * @returns Stitches instance configured for Shadow DOM
 *
 * @example
 * ```typescript
 * import { createStitchesForShadowRoot } from '@eneco/sparky/shadow';
 *
 * const shadowRoot = element.attachShadow({ mode: 'open' });
 * const { styled, css, theme, getCssText } = createStitchesForShadowRoot(shadowRoot);
 *
 * // Use styled components that inject into Shadow DOM
 * const StyledButton = styled('button', {
 *   color: '$primary',
 *   padding: '$2'
 * });
 * ```
 */
export function createStitchesForShadowRoot(shadowRoot: ShadowRoot, selectedTheme: keyof typeof themes = 'default') {
  const theme = themes[selectedTheme] as unknown as Theme;

  // Create global styles for Shadow DOM
  const applyShadowDOMGlobalStyles = () => {
    const globalCSS = globalStitches({
      ...globalStyles,
      ...normalizeCSS,
    });

    // Inject global styles into Shadow DOM
    const globalStyleElement = document.createElement('style');
    globalStyleElement.id = 'stitches-global-styles';
    globalStyleElement.textContent = globalCSS.toString();

    // Remove existing global styles
    const existingGlobalStyles = shadowRoot.querySelector('#stitches-global-styles');
    if (existingGlobalStyles) {
      existingGlobalStyles.remove();
    }
    shadowRoot.insertBefore(globalStyleElement, shadowRoot.firstChild);
  };

  // Custom getCssText that injects into Shadow DOM
  const getCssTextAndInject = () => {
    const cssText = getCssText();

    if (cssText) {
      // Create or update dynamic styles element
      let dynamicStyleElement = shadowRoot.querySelector('#stitches-dynamic-styles') as HTMLStyleElement;

      if (!dynamicStyleElement) {
        dynamicStyleElement = document.createElement('style');
        dynamicStyleElement.id = 'stitches-dynamic-styles';
        shadowRoot.appendChild(dynamicStyleElement);
      }

      dynamicStyleElement.textContent = cssText;
    }

    return cssText;
  };

  // Create theme class names for Shadow DOM
  const themeClassNames = Object.entries(themes).reduce(
    (acc, [key, value]) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      acc[key] = createTheme(value as any).className;
      return acc;
    },
    {} as Record<string, string>,
  );

  const getToken = <T extends keyof Theme>(category: T, token: keyof Theme[T]) => `var(--${category}-${String(token)})`;

  const getTokens = (): Theme => theme;

  // Apply global styles immediately
  // applyShadowDOMGlobalStyles();

  return {
    styled,
    keyframes,
    css,
    globalCss: globalStitches,
    getCssText,
    getCssTextAndInject,
    theme: defaultTheme,
    createTheme,
    config,
    themeClassNames,
    getToken,
    getTokens,
    applyShadowDOMGlobalStyles,
    shadowRoot,
  };
}

/**
 * Create a Stitches instance for Shadow DOM with automatic style injection.
 * This version automatically injects styles whenever they change.
 *
 * @param shadowRoot - The Shadow DOM root to inject styles into
 * @param selectedTheme - The theme to use (defaults to 'default')
 * @param autoInjectInterval - Auto-injection interval in ms (defaults to 100ms, set to 0 to disable)
 * @returns Stitches instance with auto-injection
 */
export function createAutoInjectingStitchesForShadowRoot(
  shadowRoot: ShadowRoot,
  selectedTheme: keyof typeof themes = 'default',
) {
  const stitchesInstance = createStitchesForShadowRoot(shadowRoot, selectedTheme);

  stitchesInstance.getCssTextAndInject();

  return stitchesInstance;
}

// Type exports for consumers
export type ShadowStitchesInstance = ReturnType<typeof createStitchesForShadowRoot>;
export type AutoInjectingShadowStitchesInstance = ReturnType<typeof createAutoInjectingStitchesForShadowRoot>;
